import { UploadDetails } from "../models";
import { plainFetcher } from "../../../../helpers/fetcher";
import {
  DOCUMENT_VALIDATOR_URL,
  CDS_NATIONAL_ID_API_URL,
  buildUrl,
  CDS_DOCUMENT_UPLOAD_API_URL,
} from "../../../../config/urls";
import { getCandidateData } from "../../../../utility/candidate-data";
import { AttachmentType } from "../models";
import { getPresignedURL } from "../../../../utility/get-presigned-url-helper";

export const updateCDSDocumentUploadMetadata = async (
  candidateId: string | undefined,
  applicationId: string,
  uploadDetails: UploadDetails
) => {
  if (!candidateId) {
    throw new Error("candidate id not found for document submission!");
  }

  const url = buildUrl(CDS_DOCUMENT_UPLOAD_API_URL, {
    candidateId,
    applicationId,
  });

  //failure in plainFetcher will result in exception thrown, UI will show error message to candidate for upload failure
  await plainFetcher(url, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(uploadDetails),
  });
};

export const submitDocument = async (
  applicationId: string,
  uploadDetailsList: UploadDetails[],
  setBlockSubmitButton: any
) => {
  setBlockSubmitButton(true);
  if (
    process.env.NODE_ENV === "development" &&
    process.env.REACT_APP_USE_MOCK_REQUESTS === "true"
  ) {
    return []; // Mock empty result for now since we don't inspect response body
  }

  for (const uploadDetails of uploadDetailsList) {
    const updatedUploadDetails = { ...uploadDetails };
    delete updatedUploadDetails.imageBase64;

    const { presignedUrl } = await getPresignedURL(
      uploadDetails.candidateId,
      CDS_DOCUMENT_UPLOAD_API_URL,
      applicationId,
      updatedUploadDetails
    );

    const header = new Headers({
      "Content-Type": uploadDetails.contentType,
    });

    const buffer =
      uploadDetails.imageBase64 &&
      Buffer.from(uploadDetails.imageBase64, "base64");

    const res = await fetch(presignedUrl, {
      method: "PUT",
      headers: header,
      body: buffer,
    });

    //Fetch does not throw catchable errors. thus we have to rely on response to check if there is a failure
    if (!res.ok) {
      throw new Error("Upload failure due to dependent service error.");
    }
  }

  //wait for all the documents are submitted, and then proceed to update metadata to CDS
  for (const uploadDetails of uploadDetailsList) {
    const updatedUploadDetails = { ...uploadDetails };
    delete updatedUploadDetails.imageBase64;
    delete updatedUploadDetails.expirationDate;
    await updateCDSDocumentUploadMetadata(
      uploadDetails.candidateId,
      applicationId,
      updatedUploadDetails
    );
  }
  setBlockSubmitButton(false);
};

export const getListAttachmentMetaData = async (applicationId: string) => {
  if (process.env.NODE_ENV === "development") {
    if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
      console.warn(
        "getListAttachmentMetaData",
        DOCUMENT_VALIDATOR_URL,
        "mocked request",
        applicationId
      );
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve([
            {
              attachmentType: AttachmentType.CURP,
              attachmentSubType: "FRONT",
              attachmentExpirationDateTime: null,
              attachmentUpdatedDateTime: "2022-11-22T16:00:47.421816Z",
              contentType: "image/jpeg",
              fileName: "curp.jpeg",
            },
          ]);
        }, 1000);
      });
    }
  }
  const { candidateId } = await getCandidateData(applicationId);
  const url = buildUrl(DOCUMENT_VALIDATOR_URL, {
    candidateId,
  });
  try {
    const res: Response = await plainFetcher(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    return res.json();
  } catch (err: any) {
    return err;
  }
};
interface IdDataParams {
  nationalIdType: AttachmentType;
  idNumber?: string;
  expirationDate?: string;
}

interface UpdateDocumentIdParams {
  applicationId: string;
  idData: IdDataParams;
  candidateId?: string;
}

export const updateDocumentId = async (params: UpdateDocumentIdParams) => {
  if (process.env.NODE_ENV === "development") {
    if (process.env.REACT_APP_USE_MOCK_REQUESTS === "true") {
      console.warn(
        "updateDocument",
        CDS_NATIONAL_ID_API_URL,
        "mocked request",
        params.applicationId
      );
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve("Succeed");
        }, 1000);
      });
    }
  }
  try {
    let candidateId = params.candidateId;
    if (!candidateId) {
      const candidateData = await getCandidateData(params.applicationId);
      candidateId = candidateData?.candidateId;
    }
    const url = buildUrl(CDS_NATIONAL_ID_API_URL, {
      candidateId,
    });
    const res = await plainFetcher(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params.idData),
    });
    return res.text();
  } catch (err) {
    return err;
  }
};
